import type { Currency } from '../models/Currency';
import type { InboundStatus } from '../models/InboundStatus';

export enum ShippingProviderType {
  DELEO = 'DELEO',
  PARXL = 'PARXL',
}

export enum ShippingStatus {
  PENDING = 'PENDING',
  PICKED_UP = 'PICKED_UP',
  IN_TRANSIT = 'IN_TRANSIT',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  RETURNED = 'RETURNED',
}

export interface ShippingData {
  provider: ShippingProviderType;
  providerOrderId: string;
  trackingNumber?: string;
  status: ShippingStatus;
  statusUpdatedAt?: string;
  labelUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ParxlTrackingEvent {
  status: string;
  date: string;
  description?: string;
  location?: string;
}

export interface ParxlData {
  plsShipmentReference: string;
  merchantBatchNo?: string;
  merchantOrderNo: string;
  orderDate: string;
  operationHub?: string;
  serviceOption: string;
  latestTrackingStatus: string;
  trackingStatus: ParxlTrackingEvent[];
  weight?: number;
  weightUnit?: string;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: string;
  };
  incoterm?: string;
  containsBattery?: boolean;
  containsDg?: boolean;
  insured?: boolean;
  cod?: boolean;
  expirationDate?: string;
}

export interface OpsComment {
  id?: string;
  timestamp?: string;
  operator: string;
  message: string;
}

export interface SourcingShipmentTrack {
  orderNumber: string; // Coupang, Naver, etc. order number - given input to Shipment Tracker
  orderDate: string; // Order date from sourcing site - output from Shipment Tracker
  productName?: string; // Product name from sourcing site (may differ from lineItem.name) - output from Shipment Tracker
  productCost?: number; // Product cost from sourcing site - output from Shipment Tracker
  trackingNumber: string; // Tracking number - output from Shipment Tracker
  deliveryCompanyCd: string; // Delivery company code - output from Shipment Tracker
  inboundArrivalDate?: string; // Inbound arrival date at warehouse - output from Shipment Tracker
  shipmentTrackUrl?: string; // Shipment tracking URL - output from Shipment Tracker
}

export interface LineItem {
  itemId?: string;
  name: string;
  nameKor?: string;
  originalName?: string; // original name in woocommerce
  originalNameKor?: string; // original korean name in woocommerce
  price: number;
  productId: string;
  parentProductId?: string;
  productPack?: string;
  productSize?: string;
  originalSku?: string; // woocommerce product sku, mostly variation sku
  sku: string; // variable product sku
  originalQuantity?: number; // original quantity in woocommerce
  quantity: number; // calculated quantity in oms
  subtotal: number;
  total: number;
  totalTax: number;
  wooCommerceImageUrl?: string;
  sourcingRoute?: string;

  // ---> deprecated fields, but still used for backward compatibility
  cpOrderNumber?: string; // tracking number in legacy
  cpOrderDate?: string;
  cpProductCost?: number;
  cpInboundArrivalDate?: string;
  cpInvoice?: string;
  // <--- deprecated fields, but still used for backward compatibility

  cpUrl?: string;
  comments?: string;
  toDelete?: boolean;
  deliveryCompanyCd?: string;
  inboundStatus?: InboundStatus;
  barcode?: string;

  sourcingShipmentTrack?: SourcingShipmentTrack;
}

export interface BillingAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone: string;
}

export interface Consignee {
  name: string;
  phone: string;
  countryCode: string;
  zipcode: string;
  address: string;
  city: string;
  state: string;
}

export interface Customer {
  id: string;
  email: string;
  name: string;
  phone: string;
}

export interface DeleoStatus {
  name: string;
  code: string;
  desc: string;
}

export interface DeleoData {
  deleoOrderId: string;
  deleoTrackingNo?: string;
  deleoStatus?: DeleoStatus;
  inboundTrackingNo?: string;
  orderTrackingList?: any[];
  orderInboundList?: any[];
  orderItemList?: any[];
  wmsInboundList?: any[];
  deleoStockStatusAtCreation?: any[];
}

export interface Order {
  orderId: string;
  orderDate: string;
  orderMonth: string;
  orderDateTime: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  status: string;
  currency: string;
  billing: BillingAddress;
  shipping: ShippingAddress;
  consignee: Consignee;
  customer: Customer;
  shippingMessage: string;
  lineItems: LineItem[];
  totalAmount: number;
  taxAmount?: number;
  discount: number;
  shippingTotal?: number;
  shippingTax?: number;
  paymentMethod?: string;
  paymentMethodTitle?: string;
  transactionId?: string;
  parentOrderId?: string;
  ipAddress?: string;
  userAgent?: string;
  pic?: string;
  deleoData?: DeleoData;
  opsComments?: OpsComment[];
  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  auditEntry?: any;
}

export interface OrderSummary {
  orderId: string;
  orderDate: string;
  orderMonth: string;
  orderDateTime: string;
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  status: string;
  consignee: {
    name: string;
    countryCode: string;
    zipcode: string;
    address: string;
    city: string;
    state: string;
  };
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  shippingMessage?: string;
  billingAddress?: OrderAddress;
  shippingAddress?: OrderAddress;
  lineItems: LineItem[];
  totalAmount: number;
  currency: Currency;
  discount: number;
  deleoData?: {
    deleoOrderId: string;
    deleoTrackingNo?: string;
    deleoStatus?: {
      name: string;
      code: string;
      desc: string;
    };
    orderItemList?: Array<{
      orderItemId: string;
      itemId: number;
      itemCd: string;
      itemNm: string;
      quantity: number;
      amt: number;
      centerCd: string;
      orgItemCd: string;
      packageItem: boolean;
      releaseDate: string | null;
      releaseQuantity: number | null;
      remark: string | null;
      status: {
        name: string;
        code: string;
        desc: string;
      };
      itemType: {
        name: string;
        code: string;
        desc: string;
      };
      wmsAsnCd: string | null;
    }>;
    orderTrackingList?: Array<{
      createdBy: string;
      createdOn: string;
      updatedBy: string;
      updatedOn: string;
      shipmentRequestId: string;
      deliveryCompanyCd: {
        name: string;
        code: string;
        desc: string;
      };
      trackingNo: string;
      wmsAsnCd: string | null;
      remark: string | null;
      deleoTrackingNo: string | null;
      orderId: string;
      requestId: string;
    }>;
    orderInboundList?: Array<{
      wmsInboundId: string;
      sellerNm: string | null;
      deliveryCompanyNm: string;
      trackingNo: string;
      itemCd: string;
      quantity: number;
      createdOn: string;
    }>;
    wmsInboundList?: Array<{
      inboundId: string;
      requestDate: string;
      trackingNo: string;
      referenceId: string;
      status: {
        name: string;
        code: string;
        desc: string;
      };
      inboundDate: string;
      deliveryCompanyCd: {
        name: string;
        code: string;
        desc: string;
      };
    }>;
  };
  opsComments?: OpsComment[];
  sourcingRoute?: string;
  cpOrderNumber?: string;
  cpOrderDate?: string;
  coCost?: number;
  cpUrl?: string;
  cpArrivalDate?: string;
  cpInvoice?: string;
  cpProductCost?: number;
  cpInboundArrivalDate?: string; // same as Deleo's Inbound Arrival Date

  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  pic?: string;
  auditEntry: AuditHistoryEntryInput;
  shippingData?: ShippingData;
  parxlData?: ParxlData;
}

export interface TrackingInfo {
  deleoTrackingNumber?: string;
  lastMileDeliveryCompany?: string;
  lastMileTrackingNumber?: string;
}

export interface CustomsNotification {
  charged: boolean;
  totalCharge?: number;
  currency?: Currency;
  description?: string;
  manualComments?: string;
}

export interface OrderListParams {
  orderMonth: string;
  countryCode?: string;
  status?: string;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface OrderListResponse {
  orders: OrderSummary[];
  lastEvaluatedKey?: string;
}

export interface InboundRequestItem {
  trackingNo: string;
  deliveryCompanyCode: string;
  itemList: Array<{
    code: string;
    quantity: number;
  }>;
}

export interface InboundRequest {
  shippingMessage?: string;
  trackingList: InboundRequestItem[];
  auditEntry: AuditHistoryEntryInput;
}

export interface DeliveryCompany {
  name: string;
  code: string;
  desc: string;
}

export interface InboundResponse {
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  orderId: string;
  custCd: string;
  shopInfoId: number;
  requestId: string;
  status: {
    name: string;
    code: string;
    desc: string;
  };
  shippingMessage: string | null;
  totalAmt: number;
  shippingFeeAmt: number | null;
  rateVersion: string | null;
  amtUnit: {
    name: string;
    code: string;
    desc: string;
  };
  serviceTypeCd: string | null;
  deliveryPaymentOption: string | null;
  orderDate: string;
  cancelDate: string | null;
  completeFailDate: string | null;
  releaseDate: string | null;
  wmsAsnCd: string | null;
  centerCd: string | null;
  groupKey: string | null;
  exportDeclarationYn: string | null;
  orgOrderId: string | null;
  orgRequestId: string | null;
  deleoTrackingNo: string;
  inboundTrackingNo: string;
  domesticDeliveryCompanyCode: string | null;
  inboundLabelType: {
    name: string;
    code: string;
    desc: string;
  };
  orderType: {
    name: string;
    code: string;
    desc: string;
  };
  sender: {
    addressName: string | null;
    name: string;
    nameEn: string | null;
    phone: string;
    cellphone: string;
    virtualPhoneNo: string | null;
    countryCd: string;
    zipcode: string;
    state: string | null;
    province: string | null;
    city: string;
    street: string;
    address1: string;
    address2: string;
    addressFull: string;
  };
  receiver: {
    addressName: string | null;
    name: string;
    phone: string;
    cellphone: string;
    virtualPhoneNo: string | null;
    email: string;
    countryCd: string;
    zipcode: string;
    state: string;
    province: string | null;
    city: string;
    street: string | null;
    address1: string;
    address2: string | null;
    addressFull: string;
    clearanceInformationType: string | null;
    clearanceInformation: string | null;
  };
  orderItemList: Array<{
    orderItemId: string;
    itemId: number;
    itemCd: string;
    orgItemCd: string;
    itemType: {
      name: string;
      code: string;
      desc: string;
    };
    itemNm: string;
    quantity: number;
    amt: number;
    releaseQuantity: number | null;
    remark: string | null;
    wmsAsnCd: string;
    centerCd: string;
    releaseDate: string | null;
    status: {
      name: string;
      code: string;
      desc: string;
    };
    packageItem: boolean;
  }>;
  orderTrackingList: Array<{
    createdBy: string;
    createdOn: string;
    updatedBy: string;
    updatedOn: string;
    shipmentRequestId: string;
    deliveryCompanyCd: DeliveryCompany;
    trackingNo: string;
    wmsAsnCd?: string;
    remark?: string;
    deleoTrackingNo?: string;
    orderId: string;
    requestId: string;
  }>;
  orderInboundList: Array<{
    wmsInboundId: string;
    sellerNm: string | null;
    deliveryCompanyNm: string | null;
    trackingNo: string;
    itemCd: string;
    quantity: number;
    createdOn: string;
  }>;
  serviceOptionList: any[];
  shopPaymentDate: string | null;
}

export interface StockStatusResponse {
  items: Array<{
    sku: string;
    quantity: number;
  }>;
}

export interface TraceItem {
  status: string;
  detailStatus: string | null;
  eventTime: string;
  empNm: string | null;
  description: string;
  location: string | null;
}

export interface TraceInfoItem {
  deliveryCompanyCode: {
    name: string;
    code: string;
    desc: string;
  };
  domesticDeliveryCompanyCode: string | null;
  trackingNo: string;
  mainTrackingNo: string;
  deleoTrackingNo: string | null;
  origin: string;
  dest: string;
  status: string;
  description: string;
  custCd: string | null;
  custType: string | null;
  deleoTrackList: any | null;
  traceList: TraceItem[];
}

export interface TraceResponse {
  orderId: string;
  status: {
    name: string;
    code: string;
    desc: string;
  };
  traceInfoList: TraceInfoItem[];
}

export interface AuditHistoryEntry {
  updatedData: any; // Could be anything that was sent to update
  operator?: string; // Only present for OPERATOR source
  source: string; // OPERATOR, DELEO, WOOCOMMERCE, etc.
  operation: string; // Operation performed (UPDATEORDER, CREATEORDER, CANCELORDER, etc.)
  updatedTimestamp: number;
  orderId: string;
}

export interface AuditHistoryEntryInput {
  operator: string;
  source: string;
}

export interface OrderAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone: string;
}

export interface OrderCustomer {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export interface OrderUpdateRequest {
  orderMonth?: string;
  orderDateTime?: string;
  countryCode?: string;
  customerId?: string;
  billingAddress?: OrderAddress;
  shippingAddress?: OrderAddress;
  customer?: OrderCustomer;
  shippingMessage?: string;
  lineItems?: LineItem[];
  totalAmount?: number;
  currency?: string;
  discount?: number;
  deleoData?: DeleoData;
  opsComments?: OpsComment[];
  pic?: string;
  nonExistentProduct?: boolean;
  requiresInbound?: boolean;
  auditEntry: AuditHistoryEntryInput;
}

export interface CloneOrderRequest {
  originalOrderId: string;
  newOrderId: string;
  newDeleoOrderId?: string;
  auditEntry: AuditHistoryEntryInput;
}

export interface SLAResult {
  status: 'WITHIN_SLA' | 'AT_RISK' | 'BREACHED' | 'COMPLETED';
  targetDays: number;
  elapsedDays: number;
  remainingDays: number;
  orderStatus: string;
}

export interface OrderFulfillmentStatus {
  order: OrderSummary;
  slaResult: SLAResult;
}

export interface FulfillmentSummary {
  withinSLA: number;
  atRisk: number;
  breached: number;
  completed: number;
}

export interface OrderFulfillmentStatusListResponse {
  items: OrderFulfillmentStatus[];
  total: number;
  summary: FulfillmentSummary;
}

export class OMSClient {
  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
  ) {}

  public async listOrders(params: OrderListParams): Promise<OrderListResponse> {
    const allOrders: OrderSummary[] = [];
    let lastEvaluatedKey = params.lastEvaluatedKey;

    do {
      const requestParams: OrderListParams = { ...params };
      if (lastEvaluatedKey) {
        requestParams.lastEvaluatedKey = lastEvaluatedKey;
      }

      const queryString = this.buildQueryString(requestParams);
      const response = await fetch(`${this.getApiBaseUrl()}/orders?${queryString}`);

      if (!response.ok) {
        console.error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
        return { orders: allOrders };
      }

      const data: OrderListResponse = await response.json();
      allOrders.push(...data.orders);
      lastEvaluatedKey = data.lastEvaluatedKey;
    } while (lastEvaluatedKey);

    return { orders: allOrders };
  }

  public async getOrderDetail(orderId: string): Promise<OrderSummary | null> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}`);
    if (!response.ok) {
      console.error(`Failed to fetch order detail: ${response.status} ${response.statusText}`);
      return null;
    }
    return await response.json();
  }

  public async updateOrder(orderId: string, request: OrderUpdateRequest): Promise<OrderSummary | null> {
    const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      console.error(`Failed to update order: ${response.status} ${response.statusText}`);
      return null;
    }
    return await this.getOrderDetail(orderId);
  }

  public async updateOrderStatus(orderId: string, status: string, operator: string): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          auditEntry: {
            source: 'OPERATOR',
            operator: operator,
          },
        }),
      });

      if (!response.ok) {
        console.error(`Failed to update order status: ${response.status} ${response.statusText}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  async requestInbound(orderId: string, inboundRequest: InboundRequest): Promise<InboundResponse> {
    try {
      console.debug('inboundRequest', inboundRequest);
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/inbound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inboundRequest),
      });

      if (!response.ok) {
        throw new Error(`Failed to request inbound: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error requesting inbound:', error);
      throw error;
    }
  }

  public async getStockStatus(skus: string): Promise<StockStatusResponse> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/stockstatus?skus=${skus}`);
      if (!response.ok) {
        throw new Error(`Failed to get stock status: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting stock status:', error);
      throw error;
    }
  }

  public async createDeleoOrder(orderId: string, auditEntry: AuditHistoryEntryInput): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/deleo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditEntry),
      });
      if (!response.ok) {
        throw new Error(`Failed to create Deleo order: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating Deleo order:', error);
      throw error;
    }
  }

  public async resetDeleoOrder(orderId: string, auditEntry: AuditHistoryEntryInput): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/deleo/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
        body: JSON.stringify(auditEntry),
      });
      if (!response.ok) {
        throw new Error(`Failed to reset Deleo order: ${response.statusText}`);
      }
      return await this.getOrderDetail(orderId);
    } catch (error) {
      console.error('Error resetting Deleo order:', error);
      throw error;
    }
  }

  async getDeliveryTrace(orderId: string): Promise<TraceResponse> {
    try {
      console.debug('getDeliveryTrace', orderId);
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/trace`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch delivery trace: ${response.status} ${response.statusText}`);
        return { orderId, status: { name: '', code: '', desc: '' }, traceInfoList: [] };
      }

      const data = await response.json();
      console.debug('getDeliveryTrace', data);
      return data;
    } catch (error) {
      console.error('Error fetching delivery trace:', error);
      return { orderId, status: { name: '', code: '', desc: '' }, traceInfoList: [] };
    }
  }

  public async cancelInbound(
    orderId: string,
    inboundId: string,
    auditEntry: AuditHistoryEntryInput,
  ): Promise<InboundResponse | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/inbound/${inboundId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ auditEntry }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel inbound: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error canceling inbound:', error);
      throw error;
    }
  }

  public async cancelOrder(
    orderId: string,
    cancelInfo: { reason: string; operator: string },
    auditEntry: AuditHistoryEntryInput,
  ): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: cancelInfo.reason,
          operator: cancelInfo.operator,
          auditEntry: auditEntry,
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to cancel order: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error canceling order:', error);
      throw error;
    }
  }

  public async listAuditHistory(orderId: string): Promise<AuditHistoryEntry[]> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/${orderId}/audit`);
      if (!response.ok) {
        throw new Error(`Failed to fetch audit history: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching audit history:', error);
      throw error;
    }
  }

  public async cloneOrder(cloneOrderRequest: CloneOrderRequest): Promise<OrderSummary | null> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cloneOrderRequest),
      });
      if (!response.ok) {
        throw new Error(`Failed to clone order: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error cloning order:', error);
      throw error;
    }
  }

  public async getOrderByTracking(trackingNo: string): Promise<OrderSummary[]> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/orders/tracking/${trackingNo}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order by tracking: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching order by tracking:', error);
      throw error;
    }
  }

  public async getFulfillmentStatus(): Promise<OrderFulfillmentStatusListResponse> {
    try {
      const response = await fetch(`${this.getApiBaseUrl()}/fulfilment/status`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch fulfillment status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching fulfillment status:', error);
      throw error;
    }
  }

  private getApiBaseUrl(): string {
    return this.apiUrl;
  }

  private buildQueryString(params: OrderListParams): string {
    const queryString = new URLSearchParams();
    queryString.append('orderMonth', params.orderMonth);
    if (params.countryCode) {
      queryString.append('countryCode', params.countryCode);
    }
    if (params.status) {
      queryString.append('status', params.status);
    }
    if (params.limit) {
      queryString.append('limit', params.limit.toString());
    }
    if (params.lastEvaluatedKey) {
      const serializedKey =
        typeof params.lastEvaluatedKey === 'object' ? JSON.stringify(params.lastEvaluatedKey) : params.lastEvaluatedKey;
      queryString.append('lastEvaluatedKey', serializedKey);
    }
    return queryString.toString();
  }
}
